import { sanitizeNullStrings } from "@/utils/sanitize";
import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import type { RollMaterial } from "../models/RollMaterial";

interface FormErrors {
	Name?: string;
}

interface RollMaterialDetailViewerProps {
	material: RollMaterial | null;
	existingMaterials: RollMaterial[];
	isSaving: boolean;
	onSave: (material: RollMaterial) => void;
	onClose: () => void;
}

export const RollMaterialDetailViewer = ({
	material,
	existingMaterials,
	isSaving,
	onSave,
	onClose,
}: RollMaterialDetailViewerProps) => {
	const [formData, setFormData] = useState<RollMaterial | null>(null);
	const [errors, setErrors] = useState<FormErrors>({});
	const [touched, setTouched] = useState<Record<string, boolean>>({});

	const isNewMaterial = !material?.Id;

	const validateForm = useMemo(() => {
		return (data: RollMaterial | null): FormErrors => {
			const newErrors: FormErrors = {};

			if (!data?.Name || data.Name.trim().length === 0) {
				newErrors.Name = "Name is required";
			} else {
				const isDuplicate = existingMaterials.some(
					(m) =>
						m.Id !== data.Id &&
						m.Name.toLowerCase() === data.Name.trim().toLowerCase(),
				);
				if (isDuplicate) {
					newErrors.Name = "This name already exists";
				}
			}

			return newErrors;
		};
	}, [existingMaterials]);

	const formErrors = useMemo(
		() => validateForm(formData),
		[formData, validateForm],
	);
	const isFormValid = Object.keys(formErrors).length === 0;
	const canCreate = isNewMaterial && isFormValid;

	useEffect(() => {
		if (material) {
			setFormData(sanitizeNullStrings({ ...material }));
		} else {
			setFormData(null);
		}
		setErrors({});
		setTouched({});
	}, [material]);

	const handleChange = (field: keyof RollMaterial, value: unknown) => {
		setFormData((prev) => (prev ? { ...prev, [field]: value } : null));
	};

	const handleBlur = (field: keyof RollMaterial) => {
		setTouched((prev) => ({ ...prev, [field]: true }));
		if (formData) {
			setErrors(validateForm(formData));
		}
	};

	const handleBlurSave = (field: keyof RollMaterial, value: unknown) => {
		handleBlur(field);

		if (isNewMaterial) return;

		const updatedData = formData ? { ...formData, [field]: value } : null;
		const currentErrors = validateForm(updatedData);
		if (currentErrors[field as keyof FormErrors]) return;

		const originalValue = material?.[field];
		const normalizedOriginal = originalValue === null ? "" : originalValue;
		const normalizedNew = value === null ? "" : value;

		if (normalizedOriginal !== normalizedNew) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCheckboxChange = (field: keyof RollMaterial, value: unknown) => {
		handleChange(field, value);

		if (isNewMaterial) return;

		if (material?.[field] !== value) {
			onSave({ ...formData!, [field]: value });
		}
	};

	const handleCreate = () => {
		setTouched({ Name: true });
		setErrors(formErrors);

		if (formData && canCreate) {
			onSave(formData);
		}
	};

	if (!material) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a roll material to view details</Typography>
			</Box>
		);
	}

	if (!formData) return null;

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={isNewMaterial ? "New Roll Material" : formData.Name}
				active={isNewMaterial ? undefined : material.Active}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					isNewMaterial ? (
						<Button
							color="primary"
							variant="contained"
							size="small"
							onClick={handleCreate}
							disabled={!canCreate || isSaving}
						>
							{isSaving ? "CREATING..." : "CREATE"}
						</Button>
					) : undefined
				}
			/>

			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Basic Information" icon={<InfoOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<TextField
									label="Name"
									value={formData.Name}
									onChange={(e) => handleChange("Name", e.target.value)}
									onBlur={(e) => handleBlurSave("Name", e.target.value)}
									fullWidth
									size="small"
									required
									error={touched.Name && !!errors.Name}
									helperText={touched.Name && errors.Name}
									sx={EDITABLE_FIELD_SX}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							</Grid2>

							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.Active}
											onChange={(e) =>
												handleCheckboxChange("Active", e.target.checked)
											}
											size="small"
										/>
									}
									label="Active"
									sx={{ ml: 0 }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>
				</Box>
			</DetailBody>
		</Box>
	);
};
